import React, { useState, useEffect, useContext } from 'react';
import RoundedButton from 'components/RoundedButton';
import { ASSET_PREFIX } from 'utils/constants';
import { getCookie } from 'utils/cookie';
import { fetchPrizeBySlug, submitPrizeChoice } from 'api/prizes';
import Link from 'next/link';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import SecondaryImagesContainer from 'components/prizes/SecondaryImagesContainer';
import PrizeOption from 'components/prizes/PrizeOption';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import { redeemTokensForReward } from 'api/tokens';
import PrizeTierBadge from 'components/prizes/PrizeTierBadge';
import { AddressForm } from './AddressForm';
import { useUIStore } from 'hooks/stores/useUIStore';
import { observer } from 'mobx-react-lite';
import { IAdditionalPromptInfo, Prize } from 'types/Prize';
import { useUserStore } from 'hooks/stores/useUserStore';

interface IProps {
  selectedPrize: any;
  closePopup: any;
  prizeWinValidationResult: any;
  tokenRedemptionOptionId: any;
  onSuccessfulChoiceHandler: any;
}

const ChoosePrizeModal = observer(
  ({
    selectedPrize,
    closePopup,
    prizeWinValidationResult,
    tokenRedemptionOptionId,
    onSuccessfulChoiceHandler,
  }: IProps) => {
    const { displayToast } = useUIStore();
    const { currentUser, setTokenBalance } = useUserStore();

    // Error message for prize confirm
    const [prizeChoiceError, setPrizeChoiceError] = useState<string | null>(
      null
    );

    // Fetch all prize details
    const [prizeDetails, setPrizeDetails] = useState<Prize | null>(null);

    const [confirmLoading, setConfirmLoading] = useState(false);

    const [selectedOption, setSelectedOption] = useState<{
      title: string;
      imageLocation: string;
    } | null>(null);

    const [confirmingPrizeChoice, setConfirmingPrizeChoice] = useState(false);

    // Object that will store mapping from prompt name to value.
    const [additionalPromptInfo, setAdditionalPromptInfo] = useState<Record<
      string,
      IAdditionalPromptInfo
    > | null>(null);

    // Used to show Giftcloud reward URL (if one is returned from API when user selects gift card prize).
    const [giftcloudRewardUrl, setGiftCloudRewardUrl] = useState(null);

    const [successfullyRedeemed, setSuccessfullyRedeemed] = useState(false);

    useEffect(() => {
      (async () => {
        const jwt = getCookie('jwt', null);
        const prize = (await fetchPrizeBySlug(jwt, selectedPrize.slug, false)).prize;
        setPrizeDetails(prize || null);
      })();
    }, []);

    const handleDismiss = () => {
      if (successfullyRedeemed) {
        // This path is only executed if choosing tokens or gift card prize where we display additional info.
        onSuccessfulChoiceHandler();
      } else {
        closePopup();
      }
    };

    const handleConfirmPrize = async () => {
      setConfirmLoading(true);
      setPrizeChoiceError(null);
      let result;
      if (additionalPromptInfo && additionalPromptInfoKeys) {
        for (let i = 0; i < additionalPromptInfoKeys.length; i++) {
          const prompt = additionalPromptInfo[additionalPromptInfoKeys[i]];
          if (prompt && prompt.value === '') {
            setPrizeChoiceError(
              'Please fill out all fields before submitting your choice.'
            );
            setConfirmLoading(false);
            return;
          }
        }
      }
      const jwt = getCookie('jwt', null);
      if (tokenRedemptionOptionId && prizeDetails) {
        const rewardValue = {
          chosenPrizeInstanceId: prizeDetails.prizeInstanceId,
          additionalPromptInfo,
          selectedOption,
        };
        result = await redeemTokensForReward(
          jwt,
          tokenRedemptionOptionId,
          1,
          rewardValue
        );
      } else {
        if (!prizeDetails) return;
        setConfirmLoading(false);
        // loop through object additionalPromptInfo check if any of its 'value fields are empty if so return false
        result = await submitPrizeChoice(
          jwt,
          prizeWinValidationResult.prizeWinId,
          prizeDetails.prizeInstanceId,
          additionalPromptInfo,
          selectedOption
        );
      }
      setConfirmLoading(false);

      if (result.successful === false) {
        handleDismiss();
        displayToast({
          title: result.reason,
          color: '#FF6600',
          timeout: 5000,
        });
        return;
      }

      switch (result.clientActionCode) {
        case 'SHOW_GIFTCLOUD_REDEMPTION_URL':
          setGiftCloudRewardUrl(result.giftcloudReward.rewardUrl);
          setSuccessfullyRedeemed(true);
          break;

        case 'SHOW_TOKEN_BALANCE_AND_LINK_TO_ACCOUNT':
          setTokenBalance(result.newTokenBalance);
          setSuccessfullyRedeemed(true);
          break;

        case null:
        default: // All other prizes
          await onSuccessfulChoiceHandler();

          displayToast({
            title:
              'Great choice! We’ll be in touch via email within 7 working days to get you your prize.',
            timeout: 9000,
          });
      }
    };

    const handleChoosePrize = async () => {
      setConfirmingPrizeChoice(true);

      if (
        prizeDetails?.dataCaptureItems &&
        prizeDetails.dataCaptureItems.length > 0
      ) {
        const additionalInfo: any = {};

        for (let i = 0; i < prizeDetails.dataCaptureItems.length; i++) {
          const prompt = prizeDetails.dataCaptureItems[i];
          additionalInfo[prompt.title] = {
            prompt,
            value: '',
          };
        }

        setAdditionalPromptInfo(additionalInfo);
      } else {
        setAdditionalPromptInfo(null);
      }
    };

    const additionalPromptInfoKeys: string[] | null =
      additionalPromptInfo && Object.keys(additionalPromptInfo);

    if (!tokenRedemptionOptionId && !prizeWinValidationResult) {
      console.log(prizeWinValidationResult, selectedPrize);
      return null;
    }

    return (
      <div
        className="popupWithDimmedBackground container"
        id="dimmedBackground"
        onClick={(e) => {
          // Asserting the target as an HTMLElement
          const target = e.target as HTMLElement;

          if (target.id === 'dimmedBackground') {
            handleDismiss();
          }
        }}
      >
        {confirmingPrizeChoice ? (
          <div
            className="message"
            id="choosePrizeModal"
          >
            <img
              id="closeButton"
              src={`${ASSET_PREFIX}/images/close_button.svg`}
              style={{ cursor: 'pointer', width: '15px' }}
              onClick={handleDismiss}
              alt="close button - cross"
            />

            <img
              src={
                selectedOption?.imageLocation ?? selectedPrize.heroImageLocation
              }
              className="thumbnail"
              style={{
                borderRadius: '5px',
                marginTop: '2em',
                maxWidth: '250px',
                width: '100%',
              }}
            />

            <p
              style={{
                fontStyle: 'italic',
                fontWeight: 'bold',
                margin: '1em',
                fontSize: '1.2em',
              }}
            >
              You've chosen
            </p>

            <div
              className="separator"
              style={{ margin: 0 }}
            />

            <p style={{ fontWeight: '900', margin: '1em', fontSize: '1.3em' }}>
              {prizeDetails && prizeDetails.title}{' '}
              {selectedOption?.title && `(${selectedOption?.title})`}
            </p>

            {additionalPromptInfoKeys &&
              additionalPromptInfoKeys.length > 0 && (
                <>
                  <div
                    className="separator"
                    style={{ margin: 0 }}
                  />
                  {additionalPromptInfoKeys.map((key, i) =>
                    additionalPromptInfo &&
                    additionalPromptInfo[key] &&
                    additionalPromptInfo[key].prompt &&
                    additionalPromptInfo[key].prompt.title.toLowerCase() !==
                      'address' ? (
                      <div
                        key={i}
                        style={{ width: '90%' }}
                      >
                        <h5 style={{ margin: '1.2em 0.5em 0.6em 0.5em' }}>
                          {additionalPromptInfo[key].prompt.title}
                        </h5>

                        <p>{additionalPromptInfo[key].prompt.description}</p>

                        <input
                          type='text'
                          required
                          placeholder={'Type here..'}
                          style={{
                            borderRadius: '3px',
                            border: '2px solid lightgray',
                            minWidth: '250px',
                            width: '100%',
                            padding: '1em',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                          }}
                          value={
                            additionalPromptInfo[
                              additionalPromptInfo[key].value
                            ] as unknown as string
                          }
                          onChange={(e) => {
                            setPrizeChoiceError(null);
                            const mutatingAdditionalPromptInfo =
                              additionalPromptInfo;
                            mutatingAdditionalPromptInfo[key].value =
                              e.target.value;
                            setAdditionalPromptInfo(
                              mutatingAdditionalPromptInfo
                            );
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <h5>
                          {additionalPromptInfo &&
                            additionalPromptInfo[key] &&
                            additionalPromptInfo[key].prompt &&
                            additionalPromptInfo[key].prompt.title}
                        </h5>
                        <p>
                          {additionalPromptInfo &&
                            additionalPromptInfo[key] &&
                            additionalPromptInfo[key].prompt &&
                            additionalPromptInfo[key].prompt.description}
                        </p>
                        <AddressForm
                          key={key}
                          onChange={(newAddress: string) => {
                            // Check if additionalPromptInfo is null or undefined
                            if (!additionalPromptInfo) return;

                            // Create a new object for the state update
                            const newAdditionalPromptInfo = {
                              ...additionalPromptInfo,
                              [key]: {
                                ...additionalPromptInfo[key],
                                value: newAddress,
                              },
                            };

                            // Update the state
                            setAdditionalPromptInfo(newAdditionalPromptInfo);
                          }}
                        />
                      </>
                    )
                  )}
                  {prizeChoiceError && (
                    <div
                      style={{
                        width: '90%',
                        fontWeight: 700,
                        color: 'red',
                        marginTop: 10,
                        textAlign: 'left',
                      }}
                    >
                      {prizeChoiceError}
                    </div>
                  )}
                </>
              )}

            {giftcloudRewardUrl ? (
              <a
                href={giftcloudRewardUrl}
                style={{ marginBottom: '2em', fontSize: '1.4em' }}
              >
                Click here to redeem your gift card! ❤️
              </a>
            ) : successfullyRedeemed ? (
              <div
                style={{
                  marginBottom: '2em',
                  textAlign: 'center',
                  fontSize: '1.2em',
                }}
              >
                <p>
                  You now have{' '}
                  <strong>{currentUser?.tokenBalance} tokens</strong>!
                </p>

                <Link href="/account?action=redeem-tokens">
                  <a style={{ fontSize: '1.1em' }}>View Redemption Options →</a>
                </Link>
              </div>
            ) : (
              <RoundedButton
                onClick={handleConfirmPrize}
                disabled={
                  (!selectedOption &&
                    prizeDetails &&
                    prizeDetails.customOptions?.length > 0) ??
                  undefined
                }
                className="chooseButton"
                loading={confirmLoading}
                style={{ width: '90%' }}
              >
                CONFIRM CHOICE
              </RoundedButton>
            )}

            <p
              className="prizeFulfillmentText"
              style={{ padding: '0 1em 1em 1em', fontStyle: 'italic' }}
            >
              By confirming your prize choice you agree to us sharing your
              details with our prize fulfillment partner who will send and
              organise delivery of your prize.
            </p>
          </div>
        ) : prizeDetails ? (
          <div
            className="message"
            id="choosePrizeModal"
          >
            <div className="heroSection">
              <img
                id="closeButton"
                src={`${ASSET_PREFIX}/images/close_button_white.svg`}
                style={{ cursor: 'pointer', width: '15px' }}
                onClick={handleDismiss}
                alt="close button - cross"
              />

              <h3 style={{ marginBottom: 0 }}>
                <strong>{prizeDetails.title}</strong>
              </h3>
              <h4 style={{ marginTop: 0 }}>{prizeDetails.subheading}</h4>

              <div
                className="blurb"
                dangerouslySetInnerHTML={{
                  __html: prizeDetails.overviewBody,
                }}
              />

              {prizeDetails.value && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    marginTop: '1em',
                  }}
                >
                  {prizeDetails.value && (
                    <PrizeTierBadge
                      prizeValue={prizeDetails.value}
                      style={undefined}
                    />
                  )}
                </div>
              )}
            </div>

            {prizeDetails.whatsIncludedBody && (
              <PaddedMaxWidthContainer>
                <p
                  style={{
                    margin: '1em 0',
                    fontSize: '1.6rem',
                    fontWeight: 800,
                  }}
                >
                  {prizeDetails.whatsIncludedHeading || <>What's Included?</>}
                </p>

                <div
                  style={{
                    marginTop: '1em',
                    fontSize: '1.25em',
                    margin: '1em',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: prizeDetails.whatsIncludedBody,
                  }}
                />

                {prizeDetails.bundleItems && (
                  <ul>
                    {prizeDetails.bundleItems?.map((bundleItem, i) => (
                      <li key={i}>{bundleItem.title}</li>
                    ))}
                  </ul>
                )}
              </PaddedMaxWidthContainer>
            )}

            <div className="paddedMaxWidthContainer">
              <SecondaryImagesContainer
                image1Location={prizeDetails.imageLocations.secondary1}
                image2Location={prizeDetails.imageLocations.secondary2}
                image1AltText={undefined}
                image2AltText={undefined}
              />

              {prizeDetails.customOptions &&
                prizeDetails.customOptions.length > 1 && (
                  <h5 style={{ width: '100%', margin: '2em 0 1em 0' }}>
                    Select An Option
                  </h5>
                )}

              {prizeDetails.customOptions &&
                prizeDetails.customOptions.map((prizeOption, i) => (
                  <PrizeOption
                    prizeOption={prizeOption}
                    key={i}
                    onClick={() => {
                      setSelectedOption(prizeOption);
                    }}
                    selected={selectedOption === prizeOption}
                  />
                ))}

              <RoundedButton
                onClick={handleChoosePrize}
                disabled={
                  !selectedOption && prizeDetails.customOptions?.length > 0
                }
                className="chooseButton"
              >
                CHOOSE{' '}
                {selectedOption ? selectedOption.title.toUpperCase() : 'PRIZE'}
              </RoundedButton>
            </div>
          </div>
        ) : (
          <PlainLoadingSpinner style={undefined} />
        )}

        <style jsx>{`
          .container {
            position: fixed;
            top: 0;
            left: 0;
            max-width: 100%;
            height: 100%;
            background-color: rgba(55, 55, 55, 0.75);
            z-index: 999;
          }

          #closeButton {
            position: fixed;
            width: 20px;
            height: 20px;
            right: 16px;
            top: 16px;
          }

          .message {
            position: fixed;
            top: 50%;
            left: 50%;
            width: 95%;
            max-width: 600px;
            max-height: 95%;
            overflow-y: auto;
            -webkit-transform: translate(-50%, -50%);
            transform: translate(-50%, -50%);

            display: flex;
            flex-direction: column;
            align-items: center;
            border-radius: 5px;
            box-shadow: 2px 2px 12px rgba(55, 55, 55, 0.6);
            background-color: white;
            z-index: 1000;
          }

          :global(#choosePrizeModal *) {
            flex-shrink: 0;
          }

          h5 {
            text-transform: none;
            letter-spacing: normal;
          }

          .heroSection {
            background-image: url(${selectedPrize.heroImageLocation});
            background-size: cover;
            background-position: center;
            width: 100%;
            min-height: 110px;
            box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.6);
            padding: 2em;
            display: flex;
            flex-direction: column;
          }

          h3,
          h4,
          .blurb {
            color: white;
            text-align: left;
            text-shadow: 0 0 6px black;
          }

          .blurb {
            font-size: 1.1em;
          }

          .secondaryImage {
            width: 48%;
            height: 100%;
          }

          ul {
            width: 100%;
            padding-left: 2em;
            margin: 0;
            font-weight: 600;
            font-size: 1.1em;
          }

          :global(.chooseButton) {
            position: relative;
            justify-self: center;
            margin: 20px;
            width: 100%;
            white-space: normal !important;
          }

          // This is needed to add padding below the button when scrolling is enabled on the parent div.
          :global(.chooseButton:after) {
            content: '';
            display: block;
            position: absolute;
            bottom: -2rem;
            width: 1px;
            height: 2rem;
          }

          :global(.prizeFulfillmentText) {
            font-style: italic;
            margin: 0 1em;
          }

          :global(.prizeFulfillmentText:after) {
            content: '';
            display: block;
            position: absolute;
            bottom: -2rem;
            width: 1px;
            height: 2rem;
          }
        `}</style>
      </div>
    );
  }
);

export default ChoosePrizeModal;
